// Inbox — Phase 2c of the revised Pamosi Clinic EMR (2026-10-03).
//
// Lists @mentions aimed at the authed clinician. Clicking "Open patient"
// jumps to /patient-records/:patientId and marks the mention read in the
// background. Clearing removes the row from the active inbox view (we
// keep read/cleared history server-side for audit).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { inboxApi } from '../lib/api';
import type { InboxMentionEntry } from '@pamosi/shared';

export function InboxScreen() {
    const qc = useQueryClient();
    const navigate = useNavigate();

    const listQ = useQuery({
        queryKey: ['inbox'],
        queryFn: () => inboxApi.list({ limit: 100 }),
    });

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['inbox'] });
    };

    const markRead = useMutation({
        mutationFn: (mentionId: string) => inboxApi.markRead(mentionId),
        onSuccess: invalidate,
    });

    const clear = useMutation({
        mutationFn: (mentionId: string) => inboxApi.clear(mentionId),
        onSuccess: invalidate,
    });

    const openPatient = (entry: InboxMentionEntry) => {
        if (!entry.read_at) markRead.mutate(entry.id);
        navigate(`/patient-records/${encodeURIComponent(entry.patient_id)}`);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-slate-900 text-2xl font-semibold">Inbox</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Notes where a teammate tagged you with @{'<your username>'}.
                    </p>
                </div>
                {listQ.data && listQ.data.unread_count > 0 && (
                    <span className="rounded-full bg-brand-50 text-brand-700 text-xs font-medium px-2 py-0.5">
                        {listQ.data.unread_count} unread
                    </span>
                )}
            </div>

            {listQ.isLoading && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">Loading…</div>
            )}
            {listQ.isError && (
                <div className="pm-card p-8 text-center text-rose-700 text-sm">
                    Could not load your inbox.
                </div>
            )}
            {listQ.data && listQ.data.entries.length === 0 && (
                <div className="pm-card p-8 text-center text-slate-500 text-sm">
                    Nothing here yet. When a teammate tags you in a shared instruction, it lands here.
                </div>
            )}

            {listQ.data && listQ.data.entries.length > 0 && (
                <ul className="space-y-2">
                    {listQ.data.entries.map((e) => (
                        <li key={e.id} className="pm-card p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {!e.read_at && (
                                            <span className="h-2 w-2 rounded-full bg-brand-500" />
                                        )}
                                        <span className="text-slate-900 font-medium">
                                            {e.patient_full_name?.trim() || 'Unnamed patient'}
                                        </span>
                                        {e.pinned && (
                                            <span className="text-xs uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                                                Pinned
                                            </span>
                                        )}
                                        {e.instruction_resolved_at && (
                                            <span className="text-xs uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                                                Resolved
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-slate-500 text-xs mt-0.5">
                                        {e.patient_phone_e164}
                                        {e.author_full_name && ` · from ${e.author_full_name}`}
                                        {` · ${formatTimestamp(e.created_at)}`}
                                    </div>
                                    <div className="text-slate-700 text-sm mt-2 whitespace-pre-wrap">
                                        {e.body_snippet}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 justify-end mt-3">
                                <button
                                    type="button"
                                    className="pm-btn-ghost text-xs"
                                    disabled={clear.isPending}
                                    onClick={() => clear.mutate(e.id)}
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    className="pm-btn-primary text-xs"
                                    onClick={() => openPatient(e)}
                                >
                                    Open patient
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <Link to="/" className="pm-btn-ghost inline-block">
                ← Back
            </Link>
        </div>
    );
}

function formatTimestamp(iso: string): string {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '';
    const diffMin = Math.round((Date.now() - t) / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 14) return `${diffDay}d ago`;
    return new Date(iso).toLocaleDateString();
}
